import React, { useState } from 'react';
import { Star } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface StarRatingProps {
  value: number;
  onChange?: (value: number) => void;
  readOnly?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const StarRating: React.FC<StarRatingProps> = ({
  value,
  onChange,
  readOnly = false,
  size = 'md',
  className,
}) => {
  const [hoverRating, setHoverRating] = useState<number | null>(null);
  
  const handleStarClick = (rating: number) => {
    if (readOnly || !onChange) return;
    
    // If clicking the same star twice, allow setting half stars
    if (Math.ceil(value) === rating && value !== rating) {
      onChange(rating); // Set to full star
    } else if (value === rating) {
      onChange(rating - 0.5); // Set to half star
    } else {
      onChange(rating);
    }
  };

  // Determine star size based on the size prop
  const starSizeClass = {
    sm: 'h-4 w-4',
    md: 'h-5 w-5',
    lg: 'h-8 w-8'
  }[size];

  return (
    <div className={cn("flex items-center space-x-1", className)}>
      {[1, 2, 3, 4, 5].map((starIndex) => {
        const ratingValue = !readOnly && hoverRating !== null ? hoverRating : value;
        
        let starFillPercentage = 0;
        if (ratingValue >= starIndex) {
          // This star is fully filled or passed
          starFillPercentage = 100;
        } else if (ratingValue > (starIndex - 1) && ratingValue < starIndex) {
          // This star is partially filled
          starFillPercentage = (ratingValue - (starIndex - 1)) * 100;
        }
        // Ensure percentage is between 0 and 100
        starFillPercentage = Math.max(0, Math.min(starFillPercentage, 100));
        
        // Determine classes based on fill percentage
        // Stroke is yellow if any fill, gray otherwise.
        // Fill base is yellow if any fill (opacity controlled), gray otherwise (will be transparent).
        const useYellow = starFillPercentage > 0;
        const finalClassName = cn(
          starSizeClass,
          !readOnly && 'cursor-pointer transition-colors',
          useYellow ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300 fill-gray-300'
        );

        return (
          <Star
            key={starIndex}
            className={finalClassName}
            style={{
              fillOpacity: starFillPercentage / 100,
              // strokeOpacity is not set, allowing text color to control stroke visibility directly
            }}
            onMouseEnter={() => !readOnly && setHoverRating(starIndex)}
            onMouseLeave={() => !readOnly && setHoverRating(null)}
            onClick={() => handleStarClick(starIndex)}
          />
        );
      })}
    </div>
  );
};

export default StarRating; 